import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HOIDONGDAODUC_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiHDDDValue } from '../../../../utils/define';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import moment from 'moment';
import HoiDongDaoDucAdd from './HoiDongDaoDucAdd';
import HoiDongDaoDucEdit from './HoiDongDaoDucEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const HoiDongDaoDuc = ({ hoSoId }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit'
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const { isLoading, data: queryData, refetch } = useQuery(
        ['hoiDongDaoDucData', hoSoId],
        () => Get(`${HOIDONGDAODUC_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    // Xử lý data từ API
    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else {
            dataList = [queryData];
        }
    }

    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const deleteFunc = useMutation((id) => DeleteSingle(HOIDONGDAODUC_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['hoiDongDaoDucData', hoSoId]);
                    queryClient.invalidateQueries(['duyetHoiDongDaoDucData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiHDDDValue.choDuyet: return <Badge variant="warning">Chờ duyệt</Badge>;
            case trangThaiHDDDValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiHDDDValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };


    return (
        <>
        <Card className="card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="font-weight-bold text-primary m-0">Hội đồng đạo đức</h5>
                    <Button variant="success" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus mr-1"></i> Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
                {isLoading ? (
                    <div className="text-center p-5 m-auto">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive flex-grow-1">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" width="50">STT</th>
                                        <th>Số quyết định</th>
                                        <th className="text-center">Ngày cấp</th>
                                        <th>Đơn vị cấp</th>
                                        <th className="text-center">File đính kèm</th>
                                        <th className="text-center">Trạng thái</th>
                                        <th>Ghi chú</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr 
                                            key={item.id}
                                            onClick={() => setSelectedRow(item)}
                                            style={{ cursor: 'pointer' }}
                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                        >
                                            <td className="text-center">{index + 1}</td>
                                            <td>{item.soQuyetDinh}</td>
                                            <td className="text-center">
                                                {item.ngayCap ? moment(item.ngayCap).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td>{item.donViCap}</td>
                                            <td className="text-center">
                                                {item.fileHDDD ? (
                                                    (() => {
                                                        const path = item.fileHDDD;
                                                        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                        const safePath = path.startsWith('/') ? path : `/${path}`;
                                                        const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                        let name = path.split('/').pop() || 'File';
                                                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                        return (
                                                            <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                                <i className="fas fa-paperclip mr-1"></i> {name}
                                                            </a>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="text-muted">Không có</span>
                                                )}
                                            </td>
                                            <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                            <td>{item.ghiChuChinhSua || ''}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Chưa có dữ liệu Hội đồng đạo đức
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        </div>

                        <ConfirmModal
                            isOpen={openConfirmDeleteModal}
                            confirmType="delete"
                            goBackAfterDelete={false}
                            handleAccept={acceptDelete}
                            handleCancel={cancelDelete}
                            content={confirmDelMessage}
                        />
                    </>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="info" 
                            size="sm" 
                            className="mr-2" 
                            onClick={() => handleEdit(selectedRow)}
                            disabled={!selectedRow || selectedRow.trangThai !== trangThaiHDDDValue.choDuyet}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => deleteAction(selectedRow.id, selectedRow.soQuyetDinh)}
                            disabled={!selectedRow || selectedRow.trangThai !== trangThaiHDDDValue.choDuyet}
                        >
                            <i className="fas fa-trash mr-1"></i> Xóa
                        </Button>
                    </div>
                </Card.Footer>
            )}
        </Card>

        {viewMode === 'add' && <HoiDongDaoDucAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />}
        {viewMode === 'edit' && dataEdit && <HoiDongDaoDucEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} />}
        </>
    );
};

export default HoiDongDaoDuc;
